import { API_BASE_URL } from "../config/env";
export { API_BASE_URL };

const ADMIN_TOKEN_KEY = "jdAdminToken";
const ADMIN_REFRESH_TOKEN_KEY = "jdAdminRefreshToken";
const LEGACY_TOKEN_KEY = "token";
const LEGACY_REFRESH_TOKEN_KEY = "refreshToken";

const getToken = () =>
	localStorage.getItem(ADMIN_TOKEN_KEY) ||
	localStorage.getItem(LEGACY_TOKEN_KEY);

const getRefreshToken = () =>
	localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY) ||
	localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);

const setTokens = (token, refreshToken) => {
	if (token) {
		localStorage.setItem(ADMIN_TOKEN_KEY, token);
	}

	if (refreshToken) {
		localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
	}

	if (token) {
		authErrorTriggered = false;
	}
};

const clearTokens = () => {
	localStorage.removeItem(ADMIN_TOKEN_KEY);
	localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
	localStorage.removeItem(LEGACY_TOKEN_KEY);
	localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
};

let authErrorHandler = null;
let authErrorTriggered = false;

export const setAuthErrorHandler = (handler) => {
	authErrorHandler = typeof handler === "function" ? handler : null;
	authErrorTriggered = false;
};

const triggerAuthError = (reason) => {
	clearTokens();

	if (authErrorTriggered) return;
	authErrorTriggered = true;

	if (typeof authErrorHandler === "function") {
		try {
			authErrorHandler(reason);
		} catch (handlerError) {
			console.error("Auth error handler threw:", handlerError);
		}
		return;
	}

	if (typeof window !== "undefined") {
		const currentPath = window.location.pathname;
		if (currentPath !== "/login") {
			window.location.replace("/login");
		}
	}
};

const makeAuthError = (message) => {
	const error = new Error(message);
	error.isAuthError = true;
	error.status = 401;
	return error;
};

const parseResponseBody = async (response) => {
	const text = await response.text();

	if (!text) {
		return {};
	}

	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
};

const getErrorMessage = (data, fallback = "Unknown error") => {
	if (!data) return fallback;

	if (typeof data === "string") {
		return data || fallback;
	}

	return (
		data?.message ||
		data?.Message ||
		data?.title ||
		data?.Title ||
		data?.error ||
		data?.Error ||
		fallback
	);
};

const handleError = async (response) => {
	let errorData = null;

	try {
		errorData = await parseResponseBody(response);
	} catch {
		errorData = null;
	}

	const errorMessage = getErrorMessage(errorData, response?.statusText);
	const error = new Error(errorMessage || "Unknown error");

	error.status = response?.status;
	error.data = errorData;

	if (response?.status === 401 || response?.status === 403) {
		error.isAuthError = true;
		triggerAuthError(`status-${response.status}`);
	}

	console.error(`HTTP error: ${response?.status} ${error.message}`);
	throw error;
};

let refreshPromise = null;

const refreshAccessToken = async () => {
	const token = getToken();
	const refreshToken = getRefreshToken();

	if (!refreshToken) {
		triggerAuthError("missing-refresh-token");
		throw makeAuthError("No refresh token available");
	}

	let response;
	try {
		response = await fetch(`${API_BASE_URL}/Admin/refresh`, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			credentials: "include",
			body: JSON.stringify({
				token: token || "",
				refreshToken,
			}),
		});
	} catch (networkError) {
		const error = new Error("Network error during token refresh");
		error.cause = networkError;
		throw error;
	}

	if (!response.ok) {
		triggerAuthError(`refresh-failed-${response.status}`);
		throw makeAuthError("Failed to refresh token");
	}

	const data = await response.json().catch(() => null);

	const newToken = data?.token || data?.Token;
	const newRefreshToken = data?.refreshToken || data?.RefreshToken;

	if (!newToken) {
		triggerAuthError("refresh-no-token");
		throw makeAuthError("Refresh response does not contain token");
	}

	setTokens(newToken, newRefreshToken);
	return newToken;
};

const refreshAccessTokenLocked = () => {
	if (!refreshPromise) {
		refreshPromise = refreshAccessToken().finally(() => {
			refreshPromise = null;
		});
	}

	return refreshPromise;
};

const DEFAULT_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 120000;
const NETWORK_RETRY_DELAY_MS = 600;

const fetchWithTimeout = (url, options) => {
	const isUpload = options?.body instanceof FormData;
	const timeoutMs = isUpload ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

	return fetch(url, { ...options, signal: controller.signal }).finally(() => {
		clearTimeout(timeoutId);
	});
};

const isTransientFetchError = (error) => {
	if (!error) return false;
	const name = error.name;
	if (name === "TypeError") return true;
	if (name === "AbortError") return false;

	const message =
		typeof error.message === "string" ? error.message.toLowerCase() : "";

	return (
		message.includes("failed to fetch") ||
		message.includes("network request failed") ||
		message.includes("load failed") ||
		message.includes("networkerror")
	);
};

const normalizeFetchError = (error) => {
	if (!error) return new Error("Network error");

	if (error.name === "AbortError") {
		const timeoutError = new Error("Запрос превысил время ожидания");
		timeoutError.cause = error;
		return timeoutError;
	}

	return error;
};

const sleep = (ms) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

const request = async (url, options = {}, skipAuthRefresh = false) => {
	const token = getToken();

	const headers = {
		...(options.headers || {}),
	};

	if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
		headers["Content-Type"] = "application/json";
	}

	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	let response = await fetchWithTimeout(`${API_BASE_URL}${url}`, {
		...options,
		credentials: "include",
		headers,
	});

	if (response.status === 401 && !skipAuthRefresh) {
		const refreshedToken = await refreshAccessTokenLocked();

		const retryHeaders = {
			...headers,
			Authorization: `Bearer ${refreshedToken}`,
		};

		response = await fetchWithTimeout(`${API_BASE_URL}${url}`, {
			...options,
			credentials: "include",
			headers: retryHeaders,
		});

		if (response.status === 401) {
			triggerAuthError("retry-still-401");
			throw makeAuthError("Session expired");
		}
	}

	return response;
};

const fetchApi = async (url, options = {}, skipAuthRefresh = false) => {
	let attempt = 0;
	const maxAttempts = 2;

	while (attempt < maxAttempts) {
		attempt += 1;

		try {
			const response = await request(url, options, skipAuthRefresh);

			if (!response.ok) {
				await handleError(response);
			}

			return await parseResponseBody(response);
		} catch (error) {
			if (attempt < maxAttempts && isTransientFetchError(error)) {
				console.warn(
					`Transient fetch error on ${url}, retrying (attempt ${attempt + 1}/${maxAttempts})`,
				);
				await sleep(NETWORK_RETRY_DELAY_MS);
				continue;
			}

			const normalized = normalizeFetchError(error);
			console.error("Fetch error:", normalized);
			throw normalized;
		}
	}
};

const unsupported = (feature, fallback) => {
	console.warn(
		`[admin-api] ${feature} is not available in current jardulik backend.`,
	);

	return fallback;
};

const buildPagedUrl = (path, params = {}) => {
	const search = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}

		search.append(key, String(value));
	});

	const suffix = search.toString();
	return suffix ? `${path}?${suffix}` : path;
};

const emptyPagedResult = (pageNumber = 1, pageSize = 10) => ({
	data: [],
	totalCount: 0,
	pageNumber,
	pageSize,
});

const normalizePagedResult = (response, pageNumber = 1, pageSize = 10) => {
	if (!response) {
		return emptyPagedResult(pageNumber, pageSize);
	}

	const data = response.data ?? response.Data ?? [];
	const totalCount =
		response.totalCount ??
		response.TotalCount ??
		response.totalRecords ??
		response.TotalRecords ??
		data.length ??
		0;

	return {
		...response,
		data,
		totalCount,
		pageNumber: response.pageNumber ?? response.PageNumber ?? pageNumber,
		pageSize: response.pageSize ?? response.PageSize ?? pageSize,
	};
};

const fetchPagedApi = async (path, params = {}) => {
	const pageNumber = params.pageNumber ?? 1;
	const pageSize = params.pageSize ?? 10;

	try {
		const response = await fetchApi(buildPagedUrl(path, params), {
			method: "GET",
		});

		return normalizePagedResult(response, pageNumber, pageSize);
	} catch (error) {
		if (error?.status === 404) {
			return emptyPagedResult(pageNumber, pageSize);
		}

		throw error;
	}
};

const getPagedCount = async (path, params = {}) => {
	try {
		const response = await fetchPagedApi(path, {
			pageNumber: 1,
			pageSize: 1,
			...params,
		});

		return response?.totalCount ?? 0;
	} catch {
		return 0;
	}
};

export const setPhoto = async (
	photoFile,
	userId,
	fileName,
	isMain,
	photoId,
	progressCallback,
) => {
	if (!photoFile) throw new Error("No photo file provided.");
	if (!userId || userId <= 0) throw new Error("Invalid user ID.");
	if (!fileName) throw new Error("File name is missing.");

	const buffer = await photoFile.arrayBuffer();
	const snapshotBlob = new Blob([buffer], {
		type: photoFile.type || "application/octet-stream",
	});

	const chunkSize = 1 * 1024 * 1024;
	const totalChunks = Math.ceil(snapshotBlob.size / chunkSize);
	const uploadId =
		typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random()}`;

	const sendChunk = (formData, chunkIndex) =>
		new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();

			xhr.open("POST", `${API_BASE_URL}/Photos/set`, true);

			const token = getToken();

			if (token) {
				xhr.setRequestHeader("Authorization", `Bearer ${token}`);
			}

			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable && typeof progressCallback === "function") {
					const chunkProgress = (event.loaded / event.total) * 100;
					const overallProgress =
						((chunkIndex + chunkProgress / 100) / totalChunks) * 100;

					progressCallback(Math.min(overallProgress, 100));
				}
			};

			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					resolve();
					return;
				}

				const error = new Error(
					`Failed to upload chunk ${chunkIndex}: ${xhr.status} ${xhr.statusText}`,
				);

				error.status = xhr.status;
				reject(error);
			};

			xhr.onerror = () =>
				reject(new Error(`Network error uploading chunk ${chunkIndex}`));

			xhr.send(formData);
		});

	const uploadChunkWithRetry = async (formData, chunkIndex, maxRetries = 3) => {
		let attempt = 0;

		const waitForRetry = (attemptNumber) =>
			new Promise((resolve) =>
				setTimeout(resolve, 300 * Math.pow(2, attemptNumber - 1)),
			);

		while (true) {
			try {
				await sendChunk(formData, chunkIndex);
				return;
			} catch (error) {
				attempt += 1;

				if (attempt > maxRetries) {
					throw error;
				}

				await waitForRetry(attempt);
			}
		}
	};

	for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
		const start = chunkIndex * chunkSize;
		const end = Math.min(start + chunkSize, snapshotBlob.size);
		const chunk = snapshotBlob.slice(start, end);

		const formData = new FormData();

		formData.append("chunk", chunk);
		formData.append("chunkIndex", String(chunkIndex));
		formData.append("totalChunks", String(totalChunks));
		formData.append("uploadId", uploadId);
		formData.append("fileName", fileName);
		formData.append("userId", String(userId));

		await uploadChunkWithRetry(formData, chunkIndex);
	}

	if (typeof progressCallback === "function") {
		progressCallback(100);
	}

	return fetchApi("/Photos/finalize-upload", {
		method: "POST",
		body: JSON.stringify({
			userId,
			photoId: photoId || 0,
			fileName,
			isMain: Boolean(isMain),
			isAdmin: true,
			sort: 0,
			uploadId,
			totalChunks,
		}),
	});
};

export const loginUser = async (loginDTO) => {
	try {
		const response = await fetchApi("/Admin/login", {
			method: "POST",
			body: JSON.stringify({
				login: loginDTO?.login ?? loginDTO?.username ?? "",
				password: loginDTO?.password ?? "",
			}),
		});

		const token = response?.token || response?.Token;
		const refreshToken = response?.refreshToken || response?.RefreshToken;

		if (token) {
			setTokens(token, refreshToken);
		}

		return response;
	} catch (error) {
		console.error("Login error:", error);
		throw error;
	}
};

export const getAdminUser = async () => {
	try {
		return await fetchApi("/Admin/getAdminUser", {
			method: "GET",
		});
	} catch (error) {
		console.error("Get admin user error:", error);
		throw error;
	}
};

export const getAllAdminUsers = async () => {
	try {
		return await fetchApi("/Admin/all", {
			method: "GET",
		});
	} catch (error) {
		console.error("Get all admin users error:", error);
		throw error;
	}
};

export const getAllUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getAll", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const getNewUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getNew", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const getEditUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getEdit", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const getFailUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getFail", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const getUsersDetails = async (userId) => {
	if (!userId || Number(userId) <= 0) {
		throw new Error("Invalid userId");
	}

	return fetchApi(buildPagedUrl("/Moderation/getDetails", { userId }), {
		method: "GET",
	});
};

export const getPhotos = async (userId) => {
	if (!userId || Number(userId) <= 0) {
		throw new Error("Invalid userId");
	}

	return fetchApi(`/Moderation/photos/${userId}`, {
		method: "GET",
	});
};

export const getSelfie = async (userId) => {
	if (!userId || Number(userId) <= 0) {
		throw new Error("Invalid userId");
	}

	return fetchApi(`/Moderation/selfie/${userId}`, {
		method: "GET",
	});
};

export const recomputeFaceMatch = async (userId) => {
	if (!userId || Number(userId) <= 0) {
		throw new Error("Invalid userId");
	}

	return fetchApi(`/Moderation/face-match/${userId}`, {
		method: "POST",
	});
};

export const updateUser = async (updateUserDTO) =>
	fetchApi("/Moderation/update", {
		method: "POST",
		body: JSON.stringify(updateUserDTO),
	});

export const aproovePhoto = async (id, options = {}) =>
	fetchApi("/Moderation/aproovePhoto", {
		method: "POST",
		body: JSON.stringify({
			id,
			suppressNotification:
				typeof options?.suppressNotification === "boolean"
					? options.suppressNotification
					: true,
		}),
	});

export const aprooveSelfie = async (id, options = {}) =>
	fetchApi("/Moderation/aprooveSelfie", {
		method: "POST",
		body: JSON.stringify({
			id,
			suppressNotification:
				typeof options?.suppressNotification === "boolean"
					? options.suppressNotification
					: true,
		}),
	});

export const deletePhoto = async (id, options = {}) =>
	fetchApi("/Moderation/deletePhoto", {
		method: "POST",
		body: JSON.stringify({
			id,
			message: typeof options?.message === "string" ? options.message : "",
			suppressNotification:
				typeof options?.suppressNotification === "boolean"
					? options.suppressNotification
					: true,
		}),
	});

export const deleteSelfie = async (id, options = {}) =>
	fetchApi("/Moderation/deleteSelfie", {
		method: "POST",
		body: JSON.stringify({
			id,
			message: typeof options?.message === "string" ? options.message : "",
			suppressNotification:
				typeof options?.suppressNotification === "boolean"
					? options.suppressNotification
					: true,
		}),
	});

export const confirmUser = async (userId, legacyOrOptions = {}) => {
	const options =
		legacyOrOptions && typeof legacyOrOptions === "object"
			? legacyOrOptions
			: {};

	return fetchApi("/Moderation/confirmUser", {
		method: "POST",
		body: JSON.stringify({
			userId,
			suppressNotification:
				typeof options?.suppressNotification === "boolean"
					? options.suppressNotification
					: true,
		}),
	});
};

export const deleteUser = async (userIdOrDto, message = "") => {
	const userId =
		typeof userIdOrDto === "object"
			? Number(userIdOrDto?.userId ?? userIdOrDto?.id ?? 0)
			: Number(userIdOrDto ?? 0);

	const customMessage =
		typeof userIdOrDto === "object"
			? String(userIdOrDto?.message ?? "")
			: String(message ?? "");
	const suppressNotification =
		typeof userIdOrDto === "object"
			? typeof userIdOrDto?.suppressNotification === "boolean"
				? userIdOrDto.suppressNotification
				: true
			: true;

	return fetchApi("/Moderation/deleteUser", {
		method: "POST",
		body: JSON.stringify({
			userId,
			message: customMessage,
			suppressNotification,
		}),
	});
};

export const blockUser = async (data) =>
	fetchApi("/Moderation/blockUser", {
		method: "POST",
		body: JSON.stringify(data),
	});

export const unBlockUser = async (userId) =>
	fetchApi("/Moderation/unBlockUser", {
		method: "POST",
		body: JSON.stringify(userId),
	});

export const getCounts = async () => {
	const [
		allUsers,
		newUsers,
		editsUsers,
		failedUsers,
		blockedUsers,
		delUsers,
		reports,
	] =
		await Promise.all([
			getPagedCount("/Moderation/getAll"),
			getPagedCount("/Moderation/getNew"),
			getPagedCount("/Moderation/getEdit"),
			getPagedCount("/Moderation/getFail"),
			getPagedCount("/Moderation/getAllBlocked"),
			getPagedCount("/Moderation/getAllDeleted"),
			getReports().catch(() => []),
		]);

	const allReports = Array.isArray(reports) ? reports.length : 0;

	const newReports = Array.isArray(reports)
		? reports.filter((item) =>
				Array.isArray(item?.reports)
					? item.reports.some((report) => !report.isViewed)
					: false,
			).length
		: 0;

	return {
		allUsers,
		newUsers,
		editsUsers,
		failedUsers,
		freezedUsers: 0,
		delUsers,
		blockedUsers,
		allReports,
		newReports,
	};
};

export const getOrders = async () =>
	fetchApi("/Orders/all", {
		method: "GET",
	});

export const getUserOrders = async (userId) =>
	fetchApi(`/UserOrders/${userId}`, {
		method: "GET",
	});

export const getAdminUserOrders = async (
	tab = "orders",
	pageNumber = 1,
	pageSize = 50,
	searchTerm = "",
) =>
	fetchPagedApi("/UserOrders/admin", {
		tab,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const createAdminPaymentLink = async ({
	userCode,
	external = false,
	amount,
	description,
	comment,
}) =>
	fetchApi("/UserOrders/admin/payment-link", {
		method: "POST",
		body: JSON.stringify({
			userCode: external ? null : Number(userCode),
			external,
			amount: Number(amount),
			description: description || null,
			comment: comment || null,
		}),
	});

export const checkAdminUserOrderStatus = async (id) =>
	fetchApi(`/UserOrders/admin/${id}/status`, {
		method: "POST",
	});

export const refundAdminUserOrder = async (id) =>
	fetchApi(`/UserOrders/admin/${id}/refund`, {
		method: "POST",
	});

export const deleteUnpaidAdminUserOrder = async (id) =>
	fetchApi(`/UserOrders/admin/${id}/delete-unpaid`, {
		method: "POST",
	});

export const setOrders = async (orderDTO) =>
	fetchApi("/Orders/update", {
		method: "POST",
		body: JSON.stringify(orderDTO),
	});

export const getConfigs = async () =>
	fetchApi("/Config/all", {
		method: "GET",
	});

export const updateConfig = async (dto) =>
	fetchApi("/Config/update", {
		method: "PUT",
		body: JSON.stringify(dto),
	});

export const getAllOrderDiscounts = async () =>
	fetchApi("/OrderDiscounts/all", {
		method: "GET",
	});

export const getOrderDiscountsByOrder = async (orderId) =>
	fetchApi(`/OrderDiscounts/order/${orderId}`, {
		method: "GET",
	});

export const upsertOrderDiscount = async (dto) =>
	fetchApi("/OrderDiscounts/upsert", {
		method: "POST",
		body: JSON.stringify(dto),
	});

export const deleteOrderDiscount = async (id) =>
	fetchApi(`/OrderDiscounts/${id}`, {
		method: "DELETE",
	});

export const createOrder = async (createOrderDTO) =>
	fetchApi("/UserOrders/createGift", {
		method: "POST",
		body: JSON.stringify(createOrderDTO),
	});

export const endOrders = async (userId) =>
	fetchApi(buildPagedUrl("/UserOrders/end", { userId }), {
		method: "POST",
	});

export const getReports = async () =>
	fetchApi("/Reports/all", {
		method: "GET",
	});

export const markReportViewed = async (reportId) =>
	fetchApi("/Reports/viewed", {
		method: "POST",
		body: JSON.stringify(reportId),
	});

export const sendToTelegram = async (data) =>
	fetchApi("/Telegram/send-notification", {
		method: "POST",
		body: JSON.stringify(data),
	});

export const sendToAllTelegram = async (data) =>
	fetchApi("/Telegram/sendToAll", {
		method: "POST",
		body: JSON.stringify(data),
	});

export const getBroadcasts = async (take = 50, skip = 0) =>
	fetchApi(`/Broadcasts/list?take=${take}&skip=${skip}`, {
		method: "GET",
	});

export const getBroadcast = async (id) =>
	fetchApi(`/Broadcasts/${id}`, {
		method: "GET",
	});

export const resumeBroadcast = async (id) =>
	fetchApi(`/Broadcasts/${id}/resume`, {
		method: "POST",
	});

export const deleteBroadcast = async (id) =>
	fetchApi(`/Broadcasts/${id}`, {
		method: "DELETE",
	});

export const getSMSCounts = async (type) =>
	fetchApi(`/SmsCounts/getCounts/${type}`, {
		method: "GET",
	});

export const getDashboardAnalytics = async () =>
	fetchApi("/Moderation/dashboard", {
		method: "GET",
	});

export const getUserAnalytics = async () =>
	getDashboardAnalytics().catch(() => ({
		sexDistribution: [],
		geoDistribution: [],
		registrationTrends: [],
		revenue: [],
		userGrowth: [],
		stats: {},
	}));

export const getFreezedUsers = async (pageNumber = 1, pageSize = 10) =>
	unsupported("Moderation/getFreezed", emptyPagedResult(pageNumber, pageSize));

export const getDellUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getAllDeleted", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});

export const getDelCounts = async () =>
	unsupported("Moderation/getDelCounts", []);

export const getTBANKData = async () =>
	unsupported("Moderation/getTBANKData", []);

export const getInfo = async () => unsupported("Info", {});

export const getMonthly = async () => unsupported("Info/monthly", []);

export const getDaily = async () => unsupported("Info/daily", []);

export const getBlockUsers = async (
	pageNumber = 1,
	pageSize = 10,
	searchTerm = "",
	country = "",
	city = "",
	sex = 0,
) =>
	fetchPagedApi("/Moderation/getAllBlocked", {
		sex,
		country,
		city,
		pageNumber,
		pageSize,
		searchTerm,
	});
export const createAdminUser = async (dto) =>
	fetchApi("/Admin/register", {
		method: "POST",
		body: JSON.stringify({
			fullName: dto.fullName ?? "",
			login: dto.login ?? "",
			password: dto.password ?? "",
			retypePassword: dto.retypePassword ?? dto.password ?? "",
			isActive: Boolean(dto.isActive),
			isSuperAdmin: Boolean(dto.isSuperAdmin),
		}),
	});

export const deleteAdminUser = async (idOrDto) => {
	const id =
		typeof idOrDto === "object" ? (idOrDto.id ?? idOrDto.adminUserId) : idOrDto;

	return fetchApi("/Admin/delete", {
		method: "DELETE",
		body: JSON.stringify({ id }),
	});
};

export const updateAdminUser = async (dto) =>
	fetchApi("/Admin/update", {
		method: "PUT",
		body: JSON.stringify({
			id: dto.id,
			fullName: dto.fullName ?? "",
			login: dto.login ?? "",
			isActive: Boolean(dto.isActive),
			isSuperAdmin: Boolean(dto.isSuperAdmin),
		}),
	});

export const resetAdminUserPassword = async (dto) =>
	fetchApi("/Admin/resetPassword", {
		method: "PUT",
		body: JSON.stringify({
			id: dto.id,
			newPassword: dto.newPassword ?? "",
			retypeNewPassword: dto.retypeNewPassword ?? dto.newPassword ?? "",
		}),
	});
